import {EventEmitter, Injectable} from "@angular/core";
import {ActivatedRouteSnapshot, Router} from "@angular/router";
import {StepNavigationElement} from "../step/step.navigation.element";

/**
 * Service with helper functions for the step navigation and etxraction of the steps of the advisor.
 */
@Injectable({
  providedIn: 'root'
})
export class StepService {
  /**
   * Stores the next and previous step.
   * @private
   */
  private steps: Steps = new Steps();

  /**
   * extra element on the left side of the navigation
   * @private
   */
  private navigationPreElement: StepNavigationElement;

  /**
   * extra element on the right side of the navigation
   * @private
   */
  private navigationPostElement: StepNavigationElement;

  /**
   * Eventemiter that is called if one of the extra navitaion elements is changed
   */
  navigationElementsChanged: EventEmitter<StepNavigationElement[]> = new EventEmitter<StepNavigationElement[]>();

  /**
   * Eventemitter when the steps are changed.
   */
  stepsChanged: EventEmitter<Steps> = new EventEmitter<Steps>();

  /**
   * eventemitter that can be used if the height of a step changes
   */
  heightChanged: EventEmitter<void> = new EventEmitter<void>();

  /**
   * eventemitter to update the navigation
   */
  updateNavigation: EventEmitter<void> = new EventEmitter<void>();

  /**
   * constructor
   * @param router
   */
  constructor(private router: Router) {}

  /**
   * extract the current, next and previous step from the route config.
   * @param snapshot
   */
  extractSteps(snapshot: ActivatedRouteSnapshot): void {
    // get the current route (index)
    const routeIndex = snapshot.routeConfig?.children?.indexOf(snapshot.firstChild.routeConfig);
    if(routeIndex != null && routeIndex > -1) {
      // get the baseURL
      let base = this.router.url.replace(snapshot.firstChild.routeConfig.path, '');
      // remove parameters from URL
      if (base.indexOf('?') !== -1) {
        base = base.slice(0, base.indexOf('?'));
      }
      snapshot.firstChild.paramMap.keys.forEach(key => {
        base.replace(snapshot.firstChild.paramMap[key], key);
      });

      // check if there is a prevoous and next step, if yes, save them
      this.steps.previousStep = routeIndex > 0 && snapshot.routeConfig.children[routeIndex - 1].path? new Step(snapshot.routeConfig.children[routeIndex - 1].path, base + snapshot.routeConfig.children[routeIndex - 1].path) : null;
      this.steps.nextStep = routeIndex < snapshot.routeConfig.children.length && snapshot.routeConfig.children[routeIndex + 1]?.path? new Step(snapshot.routeConfig.children[routeIndex + 1].path, base + snapshot.routeConfig.children[routeIndex + 1].path) : null;

      // rebuild the list of prevousSteps
      this.steps.previousStepList = [];
      let offset = routeIndex;
      while(offset > 0) {
        if(snapshot.routeConfig.children[offset - 1].path) {
          this.steps.previousStepList.push(new Step(snapshot.routeConfig.children[offset - 1].path, base + snapshot.routeConfig.children[offset - 1].path));
        }
        offset--;
      }

      // rebuild the list of next steps
      this.steps.nextStepList = [];
      offset = routeIndex;
      while(offset < snapshot.routeConfig.children.length - 1) {
        if(snapshot.routeConfig.children[offset + 1].path) {
          this.steps.nextStepList.push(new Step(snapshot.routeConfig.children[offset + 1].path, base + snapshot.routeConfig.children[offset + 1].path));
        }
        offset++;
      }

      // set the name of the current step
      this.steps.currentStep = new Step(snapshot.firstChild.routeConfig.path, base + snapshot.firstChild.routeConfig.path);

      // emit the EventEmitter to inform all childs that are subscribed.
      this.stepsChanged.emit(this.steps);
    }

    // save last Url
    this.rememberLastUrl();
  }

  /**
   * Get the currently extracted steps
   */
  getSteps(): Steps {
    return this.steps;
  }

  /**
   * add an extra navigation element to the main navigation on the left side
   * @param value
   */
  setNavigationPreElement(value: StepNavigationElement) {
    this.navigationPreElement = value;
    this.navigationElementsChanged.emit([this.navigationPreElement, this.navigationPostElement]);
  }

  /**
   * add an extra navigation element to the main navigation on the right side
   * @param value
   */
  setShowNavigationPostElement(value: StepNavigationElement) {
    this.navigationPostElement = value;
    this.navigationElementsChanged.emit([this.navigationPreElement, this.navigationPostElement]);
  }

  /**
   * save the last URL that was called in the session storage
   */
  rememberLastUrl(): void {
    // remember the last url that teh user has seen
    try {
      if (window.sessionStorage) {
        setTimeout(() => {
          const cacheKey = this.lastUrlCacheKey();
          if (cacheKey && window.sessionStorage) {
            this.router.getCurrentNavigation()
            window.sessionStorage.setItem(cacheKey, this.router.url);
          }
        }, 0);
      }
    }
    catch (e) {

    }
  }

  /**
   * returns the last url from the session storage
   */
  getLastUrl(): string {
    const cacheKey = this.lastUrlCacheKey();
    try {
      if (cacheKey && window.sessionStorage) {
        return window.sessionStorage.getItem(cacheKey);
      }
    }
    catch (e) {

    }
  }

  /**
   * create the cache key for the last url inside the session storage
   * @private
   */
  private lastUrlCacheKey(): string {
    const urlParts = this.router.url.split("/");
    if(urlParts.length > 3) {
      return 'lastUrl' + urlParts[1] + urlParts[2];
    }
    return null;
  }
}

/**
 * Class for storing the previous and next step inside a chapter.
 */
export class Steps {

  currentStep: Step;

  /**
   * previous step, will be null if non is available
   */
  previousStep: Step;

  /**
   * List of next steps, including the next and all following
   */
  previousStepList: Step[];

  /**
   * next step, will be null if non is available
   */
  nextStep: Step;

  /**
   * List of next steps, including the next and all following
   */
  nextStepList: Step[];
}

/**
 * Class for holding stepinformation for the nevigation inside chapters
 */
export class Step {
  /**
   * Name of the step
   */
  name: string;

  /**
   * the complete path of the step
   */
  path: string

  /**
   * all children
   */
  children: Step[] = [];

  /**
   * Special classes for this steps
   */
  class: string = "";

  /**
   * constructor
   * @param name
   * @param path
   */
  constructor(name: string, path: string) {
    this.name = name;
    this.path = path;
  }
}
